import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import icon_snmp from "assets/images/icons/icn_Info_solid.svg";
import icon_snmp_hover from "assets/images/icons/icn_info_solid_hover.svg";

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import useDataSourceManager from "hooks/useDataSourceManager";
import { useTheme } from "@mui/material/styles";
import { Box, Button, Typography, TextField, Tooltip } from "@mui/material";
import { cloneDeep } from "lodash";

// Component
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import { RadioButton } from 'components/buttons/RadioButton';
import GlobalAlert from "components/Notification";
import Loading from "./loading";
import MacWhitelistSection from "./components/macWhitelistSection";
import RadiusProfileSection from "./components/radiusProfileSection";
import LeaveConfirmModal from "components/Drawer/Content/Settings/Profiles/Port/leaveConfirmModal";
import LinkModal from "components/LinkModal";

// Store & Icons
import useZustandStore from "zustandStore";
import { ReactComponent as ProColorIcon } from "assets/images/icons/icn-pro.svg";
import { ReactComponent as ProOrangeOutlineIcon } from "assets/images/icons/icn_Pro_orange_outline.svg";

// Util
import {
  defaultValue,
  deviceCategoryOption,
  portTypeOption,
  linkSpeedOption,
  accessPolicyTypeDefinition,
  defaultAccessPolicyTypeList,
} from "./util/const";
import Api from "./util/api";
import Form from "./util/form";

// Const
import { formLimitation } from 'const';

const proIconStyle = {
  display: "inline-block",
  verticalAlign: "middle",
  marginLeft: 2,
  marginTop: -12,
  marginBottom: -12,
  flexShrink: 0,
};

const PortProfileModal = ({
  auth,
  openModal,
  handleCloseModal,
  onSaveChanges,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const api = Api.init(auth);
  const form = Form.init(theme);
  const isUnityProSubscribed = useZustandStore((state) => state.isUnityProSubscribed);

  // State
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formValue, setFormValue] = useState(null);
  const [openLeaveModal, setOpenLeaveModal] = useState(false);
  const [leaveProfileType, setLeaveProfileType] = useState("schedule"); // schedule, radius-server, mac-address
  const [leaveModalTitle, setLeaveModalTitle] = useState("");
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [radiusProfiles, setRadiusProfiles] = useState([]);
  const [macProfiles, setMacProfiles] = useState([]);
  const [localUuidList] = useState({
    create: uuidv4(),
    readSchedule: uuidv4(),
    readRadiusProfile: uuidv4(),
    readMacProfile: uuidv4(),
  });
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  const radiusProfileOptions = useMemo(() => {
    return radiusProfiles
      .filter((item) => item.id && item.name)
      .map(({ id, name }) => ({
        value: id,
        label: name,
      }));
  }, [radiusProfiles]);

  const macProfileOptions = useMemo(() => {
    return macProfiles
      .filter((item) => item.id && item.name)
      .map((item) => ({
        value: item.id,
        label: item.name,
        macCount: item.macList?.length ?? item.macAddresses?.length ?? 0,
      }));
  }, [macProfiles]);

  // Method
  const init = () => {
    setLoading(true);
    setFormValue(cloneDeep(defaultValue));
    setLoading(false);
  }

  const handleChangeFormValue = (key, value) => {
    setFormValue((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleChangeMultiFormValues = (updateData) => {
    setFormValue((prev) => ({
      ...prev,
      ...updateData,
    }));
  };

  const fetchScheduleOptions = () => {
    const { apiKey, params } = api.getScheduleRequestParams(Api.actionTypeEnum.read);
    sendRequest(apiKey, localUuidList.readSchedule, params);
  };

  const fetchRadiusProfiles = () => {
    const { apiKey, params } = api.getRadiusProfileRequestParams(Api.actionTypeEnum.read);
    sendRequest(apiKey, localUuidList.readRadiusProfile, params);
  };

  const fetchMacProfiles = () => {
    const { apiKey, params } = api.getMacProfileRequestParams(Api.actionTypeEnum.read);
    sendRequest(apiKey, localUuidList.readMacProfile, params);
  };

  const handlePortScheduleChange = (opt) => {
    if (opt.value === "CREATE_NEW") {
      setLeaveProfileType("schedule");
      setLeaveModalTitle("Create New Schedule");
      setOpenLeaveModal(true);
    } else {
      handleChangeFormValue("portSchedule", opt.value);
    }
  };

  const handlePoeScheduleChange = (opt) => {
    if (opt.value === "CREATE_NEW") {
      setLeaveProfileType("schedule");
      setLeaveModalTitle("Create New Schedule");
      setOpenLeaveModal(true);
    } else {
      handleChangeFormValue("poeSchedule", opt.value);
    }
  };

  const handleMacProfileChange = (opt) => {
    if (opt.value === "Edit/Create New Profile") {
      setLeaveProfileType("mac-address");
      setLeaveModalTitle("Edit MAC Address Profile");
      setOpenLeaveModal(true);
    } else {
      handleChangeFormValue("accessPolicy", {
        ...formValue.accessPolicy,
        macList: opt.value === "" ? null : opt.value,
      });
    }
  };

  const handleRadiusProfileChange = (opt) => {
    if (opt.value === "Edit/Create New Profile") {
      setLeaveProfileType("radius-server");
      setLeaveModalTitle("Edit RADIUS Profile");
      setOpenLeaveModal(true);
    } else {
      handleChangeFormValue("accessPolicy", {
        ...formValue.accessPolicy,
        radiusProfile: opt.value === "" ? null : opt.value,
      });
    }
  };

  const handleLeaveConfirm = () => {
    setOpenLeaveModal(false);
    let targetTab = "profiles-schedule";
    if (leaveProfileType === "radius-server") {
      targetTab = "profiles-radius-server";
    } else if (leaveProfileType === "mac-address") {
      targetTab = "profiles-mac-address";
    }
    navigate(location.pathname, {
      state: { navigateTab: targetTab },
    });
  };

  const checkFormValid = () => {
    if (!formValue || !formValue.profileName || formValue.profileName.trim() === "") {
      return false;
    }

    if (
      (formValue.deviceCategory === deviceCategoryOption.ap.value)
      || (
        formValue.deviceCategory === deviceCategoryOption.switch.value
        && formValue.portType === portTypeOption.trunk.value
      )
    ) {
      if (!form.checkAllowVlans(formValue.allowVlan?.trim())) {
        return false;
      }
    }

    if (!form.checkPvid(formValue.pvid?.toString().trim())) {
      return false;
    }

    // Dynamic size limit check
    if (
      formValue.portType === portTypeOption.access.value &&
      formValue.accessPolicy?.enableState &&
      formValue.accessPolicy?.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value
    ) {
      const dynamicSize = formValue.accessPolicy?.dynamicSize;
      if (dynamicSize == null || !form.checkDynamicSize(dynamicSize)) {
        return false;
      }
    }

    return true;
  }

  const handleSave = () => {
    if (!formValue) return;

    if (!isUnityProSubscribed) {
      const isSwitch = formValue.deviceCategory === deviceCategoryOption.switch.value;
      const isAccessPort = isSwitch && formValue.portType === portTypeOption.access.value;
      const isAccessPolicyEnabled = formValue.accessPolicy?.enableState === true;

      if (isAccessPort && isAccessPolicyEnabled) {
        setIsLinkModalOpen(true);
        return;
      }
    }

    setLoading(true);
    const payload = defaultValue.makePayload(formValue);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.create);
    sendRequest(
      apiKey,
      localUuidList.create,
      params,
      payload,
    );
  }

  // Side effect
  useEffect(() => {
    if (openModal) {
      init();
    } else {
      setFormValue(cloneDeep(defaultValue));
    }
  }, [openModal]);

  useEffect(() => {
    if (openModal && auth.scopeLevel) {
      fetchScheduleOptions();
      fetchRadiusProfiles();
      fetchMacProfiles();
    }
  }, [openModal, auth.scopeLevel]);

  useEffect(() => {
    if (!dataMap?.[localUuidList.readSchedule]) return;

    const readResponse = getData(localUuidList.readSchedule);
    if (readResponse) {
      setScheduleOptions((readResponse.data || []).map((p) => ({ label: p.name, value: p.id })));
    }
  }, [dataMap?.[localUuidList.readSchedule]]);

  useEffect(() => {
    if (!dataMap?.[localUuidList.readRadiusProfile]) return;

    const readResponse = getData(localUuidList.readRadiusProfile);
    if (readResponse && readResponse.data) {
      setRadiusProfiles(readResponse.data);
    }
  }, [dataMap?.[localUuidList.readRadiusProfile]]);

  useEffect(() => {
    if (!dataMap?.[localUuidList.readMacProfile]) return;

    const readResponse = getData(localUuidList.readMacProfile);
    if (readResponse && readResponse.data) {
      setMacProfiles(readResponse.data);
    }
  }, [dataMap?.[localUuidList.readMacProfile]]);

  useEffect(() => {
    if (!dataMap?.[localUuidList.create]) return;

    const createResponse = getData(localUuidList.create);

    if (createResponse) {
      if (createResponse.hasOwnProperty("code") && createResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: createResponse?.message,
        });
        setLoading(false);
      } else {
        onSaveChanges();
      }
    }
  }, [dataMap?.[localUuidList.create]]);

  if (!openModal) {
    return null;
  }

  return (
    <>
      <ModalContainer display={openModal} modalWidth="auto">
        {loading && (
          <Loading />
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            Create New Port Profile
          </Typography>
          <Button
            onClick={handleCloseModal}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={form.getRowStyle()}>
          <Box sx={{
            width: "100%",
            color: theme.palette.primary.greySecound,
            fontSize: theme.typography.customFontSize[2],
          }}>
            ✦ Available features may vary depending on the device model
          </Box>
        </Box>

        {/* Device Type */}
        <Box sx={form.getRowStyle()}>
          <Box sx={form.getLabelStyle()}>Device Type</Box>
          {
            deviceCategoryOption.convertToForm().map((item, index) => (
              <RadioButton
                key={index}
                label={item.label}
                value={item.value}
                checked={formValue.deviceCategory === item.value}
                onChange={() => handleChangeFormValue("deviceCategory", item.value)}
                name="deviceCategory"
              />
            ))
          }
        </Box>
        {/* Profile Name */}
        <Box sx={form.getRowStyle()}>
          <Box sx={form.getLabelStyle()}>Profile Name</Box>
          <TextField
            placeholder="e.g. My Office"
            sx={form.getTextfieldStyle(true)}
            value={formValue.profileName}
            onChange={(e) => handleChangeFormValue("profileName", e.target.value)}
          />
        </Box>
        {/* Port State */}
        {
          formValue.deviceCategory === deviceCategoryOption.switch.value && (
            <>
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Port State</Box>
                <RadioButton
                  label="Enabled"
                  value={true}
                  checked={formValue.portState === true && !formValue.isPortScheduleMode}
                  onChange={() => {
                    handleChangeMultiFormValues({
                      portState: true,
                      isPortScheduleMode: false,
                    });
                  }}
                  name="portState"
                />
                <RadioButton
                  label="Disabled"
                  value={false}
                  checked={formValue.portState === false && !formValue.isPortScheduleMode}
                  onChange={() => {
                    handleChangeMultiFormValues({
                      portState: false,
                      isPortScheduleMode: false,
                    });
                  }}
                  name="portState"
                />
                <RadioButton
                  label="Schedule"
                  value="schedule"
                  checked={formValue.isPortScheduleMode}
                  onChange={() => {
                    handleChangeMultiFormValues({
                      isPortScheduleMode: true,
                      portState: true,
                    });
                  }}
                  name="portState"
                />
              </Box>
              {formValue.isPortScheduleMode && (
                <Box sx={form.getRowStyle()}>
                  <Box sx={form.getLabelStyle()}></Box>
                  <MenuSelect
                    value={formValue.portSchedule}
                    onChange={handlePortScheduleChange}
                    options={scheduleOptions}
                    placeholder="Select a Schedule Profile"
                    width={form.getTextfieldStyle().width}
                    pinningOptions={[
                      {
                        value: "CREATE_NEW",
                        label: "Create New Schedule",
                        showArrow: true,
                      }
                    ]}
                  />
                </Box>
              )}
              {/* Port Type */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Port Type</Box>
                {
                  portTypeOption.convertToForm().map((item, index) => (
                    <RadioButton
                      key={index}
                      label={item.label}
                      value={item.value}
                      checked={formValue.portType === item.value}
                      onChange={() => handleChangeFormValue("portType", item.value)}
                      name="portType"
                    />
                  ))
                }
              </Box>
            </>
          )
        }
        {/* PVID */}
        <Box sx={form.getRowStyle()}>
          <Box sx={form.getLabelStyle()}>PVID</Box>
          <TextField
            sx={form.getTextfieldStyle(!!!formValue.pvid ? true : form.checkPvid(formValue.pvid?.toString().trim()))}
            value={formValue.pvid}
            onChange={(e) => { handleChangeFormValue("pvid", e.target.value); }}
          />
          <Typography
            sx={{
              color: theme.palette.primary.greySecound,
              fontSize: theme.typography.customFontSize[1],
            }}
          >
            (1~4094)
          </Typography>
        </Box>
        {
          (
            (formValue.deviceCategory === deviceCategoryOption.ap.value)
            || (
              formValue.deviceCategory === deviceCategoryOption.switch.value
              && formValue.portType === portTypeOption.trunk.value
            )
          ) && (
            <>
              {/* Allowed VLANs */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Allowed VLANs</Box>
                <TextField
                  placeholder="e.g. 1,4-7"
                  sx={form.getTextfieldStyle(!!!formValue.allowVlan ? true : form.checkAllowVlans(formValue.allowVlan?.trim()))}
                  value={formValue.allowVlan}
                  onChange={(e) => handleChangeFormValue("allowVlan", e.target.value)}
                />
                <Typography
                  sx={{
                    color: theme.palette.primary.greySecound,
                    fontSize: theme.typography.customFontSize[1],
                  }}
                >
                  {/* (1~4094) */}
                  {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                </Typography>
              </Box>
            </>
          )
        }
        {
          formValue.deviceCategory === deviceCategoryOption.switch.value && (
            <>
              {/* Copper Link Speed */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Copper Link Speed </Box>
                <MenuSelect
                  value={formValue.linkSpeed}
                  onChange={(opt) => {
                    handleChangeFormValue("linkSpeed", opt.value);
                  }}
                  options={linkSpeedOption.convertToForm()}
                  width={form.getTextfieldStyle().width}
                  additionZIndex={1}
                />
              </Box>
              {/* Fiber Link Speed */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Fiber Link Speed </Box>
                <MenuSelect
                  value={formValue.fiberLinkSpeed}
                  onChange={(opt) => {
                    handleChangeFormValue("fiberLinkSpeed", opt.value);
                  }}
                  options={linkSpeedOption.convertToForm()}
                  width={form.getTextfieldStyle().width}
                />
              </Box>
              {/* DDP */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>DDP</Box>
                <RadioButton
                  label="Enable"
                  value={true}
                  checked={formValue.ddpState === true}
                  onChange={() => handleChangeFormValue("ddpState", true)}
                  name="ddpState"
                />
                <RadioButton
                  label="Disable"
                  value={false}
                  checked={formValue.ddpState === false}
                  onChange={() => handleChangeFormValue("ddpState", false)}
                  name="ddpState"
                />
              </Box>
              {/* LBD */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>LBD</Box>
                <RadioButton
                  label="Enable"
                  value={true}
                  checked={formValue.lbdState === true}
                  onChange={() => handleChangeFormValue("lbdState", true)}
                  name="lbdState"
                  disabled={formValue.rstpState === true}
                />
                <RadioButton
                  label="Disable"
                  value={false}
                  checked={formValue.lbdState === false}
                  onChange={() => handleChangeFormValue("lbdState", false)}
                  name="lbdState"
                  disabled={formValue.rstpState === true}
                />
              </Box>
              {/* RSTP */}
              <Box sx={form.getRowStyle()}>
                <Box sx={{ ...form.getLabelStyle(), display: "flex", alignItems: "center" }}>
                  RSTP
                  <Tooltip title="When RSTP is enabled as the primary loop prevention mechanism, LBD should not be enabled." placement="top">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: 16,
                        ml: "5px",
                        "& img": {
                          content: `url(${icon_snmp})`,
                        },
                        "&:hover img": {
                          content: `url(${icon_snmp_hover})`,
                        },
                      }}
                    >
                      <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
                    </Box>
                  </Tooltip>
                </Box>
                <RadioButton
                  label="Enable"
                  value={true}
                  checked={formValue.rstpState === true}
                  onClick={() => handleChangeMultiFormValues({
                    rstpState: true,
                    lbdState: false,
                  })}
                  name="rstpState"
                />
                <RadioButton
                  label="Disable"
                  value={false}
                  checked={formValue.rstpState === false}
                  onClick={() => handleChangeMultiFormValues({
                    rstpState: false,
                    stpState: false,
                  })}
                  name="rstpState"
                />
              </Box>
              {/* STP Guard */}
              {formValue.rstpState === true && (
                <Box sx={form.getRowStyle()}>
                  <Box sx={form.getLabelStyle()}>STP Guard</Box>
                  <RadioButton
                    label="Enable"
                    value={true}
                    checked={formValue.stpState === true}
                    onChange={() => handleChangeFormValue("stpState", true)}
                    name="stpState"
                  />
                  <RadioButton
                    label="Disable"
                    value={false}
                    checked={formValue.stpState === false}
                    onChange={() => handleChangeFormValue("stpState", false)}
                    name="stpState"
                  />
                </Box>
              )}
              {/* PoE State */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>PoE State</Box>
                <RadioButton
                  label="Enabled"
                  value={true}
                  checked={formValue.poeState === true && !formValue.isPoeScheduleMode}
                  onChange={() => {
                    handleChangeMultiFormValues({
                      poeState: true,
                      isPoeScheduleMode: false,
                    });
                  }}
                  name="poeState"
                />
                <RadioButton
                  label="Disabled"
                  value={false}
                  checked={formValue.poeState === false && !formValue.isPoeScheduleMode}
                  onChange={() => {
                    handleChangeMultiFormValues({
                      poeState: false,
                      isPoeScheduleMode: false,
                    });
                  }}
                  name="poeState"
                />
                <RadioButton
                  label="Schedule"
                  value="schedule"
                  checked={formValue.isPoeScheduleMode}
                  onChange={() => {
                    handleChangeMultiFormValues({
                      isPoeScheduleMode: true,
                      poeState: true,
                    });
                  }}
                  name="poeState"
                />
              </Box>
              {formValue.isPoeScheduleMode && (
                <Box sx={form.getRowStyle()}>
                  <Box sx={form.getLabelStyle()}></Box>
                  <MenuSelect
                    value={formValue.poeSchedule}
                    onChange={handlePoeScheduleChange}
                    options={scheduleOptions}
                    placeholder="Select a PoE Profile"
                    width={form.getTextfieldStyle().width}
                    pinningOptions={[
                      {
                        value: "CREATE_NEW",
                        label: "Create New Schedule",
                        showArrow: true,
                      }
                    ]}
                  />
                </Box>
              )}

              {/* Access Policy */}
              {formValue.portType === portTypeOption.access.value && (
                <>
                  <Box sx={form.getRowStyle()}>
                    <Box sx={{ ...form.getLabelStyle(), display: "flex", alignItems: "center" }}>
                      Access Policy
                      {isUnityProSubscribed || !formValue.accessPolicy?.enableState ? (
                        <ProColorIcon aria-label="Pro" style={proIconStyle} />
                      ) : (
                        <Tooltip
                          title="Pro feature disabled"
                          placement="bottom"
                          arrow
                        >
                          <ProOrangeOutlineIcon aria-label="Pro" style={proIconStyle} />
                        </Tooltip>
                      )}
                    </Box>
                    <MenuSelect
                      value={
                        formValue.accessPolicy?.enableState
                          ? formValue.accessPolicy.type
                          : accessPolicyTypeDefinition.DISABLED.value
                      }
                      onChange={(opt) => {
                        handleChangeFormValue("accessPolicy", {
                          ...formValue.accessPolicy,
                          enableState: opt.value !== accessPolicyTypeDefinition.DISABLED.value,
                          type: opt.value === accessPolicyTypeDefinition.DISABLED.value
                            ? accessPolicyTypeDefinition.STATIC.value
                            : opt.value,
                        });
                      }}
                      options={defaultAccessPolicyTypeList}
                      width="360px"
                    />
                  </Box>

                  {formValue.accessPolicy?.enableState &&
                    (formValue.accessPolicy?.type === accessPolicyTypeDefinition.STATIC.value ||
                      formValue.accessPolicy?.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value) && (
                      <MacWhitelistSection
                        macProfile={formValue.accessPolicy.macList || null}
                        onChangeMacProfile={handleMacProfileChange}
                        macProfileOptions={macProfileOptions}
                        macProfilesRaw={macProfiles}
                        onEditMacProfile={() => {
                          setLeaveProfileType("mac-address");
                          setLeaveModalTitle("Edit MAC Address Profile");
                          setOpenLeaveModal(true);
                        }}
                        showEditGeneral={true}
                        showDynamicSize={formValue.accessPolicy?.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value}
                        dynamicSize={formValue.accessPolicy?.dynamicSize}
                        onChangeDynamicSize={(val) => {
                          handleChangeFormValue("accessPolicy", {
                            ...formValue.accessPolicy,
                            dynamicSize: val,
                          });
                        }}
                        form={form}
                      />
                    )}

                  {formValue.accessPolicy?.enableState &&
                    formValue.accessPolicy?.type === accessPolicyTypeDefinition["8021X_PROFILE"].value && (
                      <RadiusProfileSection
                        radiusProfile={formValue.accessPolicy.radiusProfile || null}
                        onChangeRadiusProfile={handleRadiusProfileChange}
                        radiusProfileOptions={radiusProfileOptions}
                        radiusProfilesRaw={radiusProfiles}
                        onEditRadiusProfile={() => {
                          setLeaveProfileType("radius-server");
                          setLeaveModalTitle("Edit RADIUS Profile");
                          setOpenLeaveModal(true);
                        }}
                        showEditGeneral={true}
                      />
                    )}
                </>
              )}
            </>
          )
        }
        <Box
          sx={{
            margin: "24px 0 16px auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            onClick={handleCloseModal}
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleSave}
            width="100px"
            height="32px"
            disabled={!checkFormValid()}
          >
            Create
          </SolidButton>
        </Box>
      </ModalContainer>
      <LeaveConfirmModal
        isOpen={openLeaveModal}
        onClose={() => setOpenLeaveModal(false)}
        leaveCallback={handleLeaveConfirm}
        profileType={leaveProfileType}
        title={leaveModalTitle}
      />
      <LinkModal
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? "/msp/license" : "/license");
        }}
      />
      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default PortProfileModal;